/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { HoverTips } from '@/components/HoverTips'
import { useTranslateAmount } from '@/hooks/useTranslateAmount'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { AvailableState } from '@/pages/billing/IncomeExpense/AvailableState'
import { BalanceWarning } from '@/pages/billing/IncomeExpense/BalanceWarning'
import { CASH_DETAILS, CashDetails } from '@/pages/billing/IncomeExpense/CashDetails'
import { FROZEN_DETAILS, FrozenDetails } from '@/pages/billing/IncomeExpense/FrozenDetails'
import { RechargeRemittance } from '@/pages/billing/IncomeExpense/RechargeRemittance'
import { AvailableBalanceState } from '@/pages/billing/interface'
import { accountClient } from '@/services/cloudClient/accountClient'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { ModelAvailableBalance } from 'account-client'
import { Space } from 'antd'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router-dom'
import styles from './index.less'

export function IncomeExpense() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { contractId } = useParams()
  const { currency, translateAmount } = useTranslateAmount()
  const [availableBalance, setAvailableBalance] = useState<ModelAvailableBalance>()

  const availableBalanceClassNames = useMemo(() => {
    const names = ['no-wrap', styles.balanceAmount]
    if (!availableBalance) {
      return names
    }
    switch (availableBalance.state.summary) {
      case AvailableBalanceState.normal:
        return names
      case AvailableBalanceState.overdraft:
        return [...names, styles.overdraft]
      default:
        return [...names, styles.arrear]
    }
  }, [availableBalance])

  useEffect(() => {
    accountClient.availableBalanceApi.getAvailableBalance(contractId).then(res => setAvailableBalance(res.data))
  }, [])

  return (
    <LayoutOthers.MainContent title={t('Overview')}>
      <LayoutOthers.ContentCard title={t('Available Balance')}>
        <div className={styles.availableBalance}>
          {availableBalance && availableBalance.state.summary !== AvailableBalanceState.normal && (
            <AvailableState availableBalance={availableBalance} />
          )}
          <Space size="large">
            <strong className={classNames(availableBalanceClassNames)}>
              {availableBalance?.availableBalanceFormatted}
              <span className="small-currency"> {currency}</span>
            </strong>
            <RechargeRemittance />
            <BalanceWarning />
          </Space>
        </div>
        <div className={styles.itemsBox}>
          <div className={styles.items}>
            <Space direction="vertical" align="center">
              <strong>
                <HoverTips title={t('Amount recharged(the remaining balance after the bill deduction)')}>{t('Cash Balance')}</HoverTips>
                <Link to={`${pathname}#${CASH_DETAILS}`}> #</Link>
              </strong>
              <div>{translateAmount(availableBalance?.details.bankBalanceFormatted)}</div>
            </Space>
            <PlusOutlined />
            <Space direction="vertical" align="center">
              <strong>
                <HoverTips title={t('Total amount of valid and available vouchers')}>{t('Voucher Balance')}</HoverTips>
                <Link to={`/billing-center/${contractId}/vouchers`}> #</Link>
              </strong>
              <div>{translateAmount(availableBalance?.details.voucherFormatted)}</div>
            </Space>
            <MinusOutlined />
            <Space direction="vertical" align="center">
              <strong>
                <HoverTips title={t('frozen_total_amount_desc')}>{t('Frozen Total Amount')}</HoverTips>
                <Link to={`${pathname}#${FROZEN_DETAILS}`}> #</Link>
              </strong>
              <div>{translateAmount(availableBalance?.details.frozenFormatted)}</div>
            </Space>
            <MinusOutlined />
            <Space direction="vertical" align="center">
              <HoverTips title={`${t('Statement Date')}: ${t('2nd of each month')}`}>
                <strong>{t('Unbilled Amount')}</strong>
              </HoverTips>
              <div>{translateAmount(availableBalance?.details.unbilledFormatted)}</div>
            </Space>
            <MinusOutlined />
            <Space direction="vertical" align="center">
              <HoverTips title={`${t('Deducted on')}: ${t('7th of each month')}`}>
                <strong>{t('Due Amount')}</strong>
              </HoverTips>
              <div>{translateAmount(availableBalance?.details.undeductedDueFormatted)}</div>
            </Space>
            <MinusOutlined />
            <Space direction="vertical" align="center">
              <strong className="red-text">{t('Overdue Amount')}</strong>
              <div className="red-text">{translateAmount(availableBalance?.details.undeductedOverDueFormatted)}</div>
            </Space>
          </div>
        </div>
      </LayoutOthers.ContentCard>
      <CashDetails />
      {/*<VoucherDetails />*/}
      <FrozenDetails />
    </LayoutOthers.MainContent>
  )
}
